
import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, isToday, isPast, isFuture, subDays } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactCountryFlag from 'react-country-flag';

const countryCodeToIso: Record<string, string> = {
  '+1': 'US',
  '+20': 'EG',
  '+27': 'ZA',
  '+30': 'GR',
  '+31': 'NL',
  '+32': 'BE',
  '+33': 'FR',
  '+34': 'ES',
  '+36': 'HU',
  '+39': 'IT',
  '+40': 'RO',
  '+41': 'CH',
  '+43': 'AT',
  '+44': 'GB',
  '+45': 'DK',
  '+46': 'SE',
  '+47': 'NO',
  '+48': 'PL',
  '+49': 'DE',
  '+51': 'PE',
  '+52': 'MX',
  '+53': 'CU',
  '+54': 'AR',
  '+55': 'BR',
  '+56': 'CL',
  '+57': 'CO',
  '+58': 'VE',
  '+60': 'MY',
  '+61': 'AU',
  '+62': 'ID',
  '+63': 'PH',
  '+64': 'NZ',
  '+65': 'SG',
  '+66': 'TH',
  '+81': 'JP',
  '+82': 'KR',
  '+84': 'VN',
  '+86': 'CN',
  '+90': 'TR',
  '+91': 'IN',
  '+92': 'PK',
  '+93': 'AF',
  '+94': 'LK',
  '+95': 'MM',
  '+98': 'IR',
  '+212': 'MA',
  '+213': 'DZ',
  '+216': 'TN',
  '+218': 'LY',
  '+220': 'GM',
  '+221': 'SN',
  '+233': 'GH',
  '+234': 'NG',
  '+254': 'KE',
  '+255': 'TZ',
  '+256': 'UG',
  '+260': 'ZM',
  '+263': 'ZW',
  '+351': 'PT',
  '+352': 'LU',
  '+353': 'IE',
  '+354': 'IS',
  '+355': 'AL',
  '+358': 'FI',
  '+359': 'BG',
  '+370': 'LT',
  '+371': 'LV',
  '+372': 'EE',
  '+380': 'UA',
  '+381': 'RS',
  '+385': 'HR',
  '+386': 'SI',
  '+420': 'CZ',
  '+421': 'SK',
  '+593': 'EC',
  '+852': 'HK',
  '+853': 'MO',
  '+855': 'KH',
  '+856': 'LA',
  '+880': 'BD',
  '+886': 'TW',
  '+960': 'MV',
  '+961': 'LB',
  '+962': 'JO',
  '+963': 'SY',
  '+964': 'IQ',
  '+965': 'KW',
  '+966': 'SA',
  '+967': 'YE',
  '+968': 'OM',
  '+971': 'AE',
  '+972': 'IL',
  '+973': 'BH',
  '+974': 'QA',
  '+975': 'BT',
  '+976': 'MN',
  '+977': 'NP',
  '+992': 'TJ',
  '+993': 'TM',
  '+994': 'AZ',
  '+995': 'GE',
  '+996': 'KG',
  '+998': 'UZ',
};

const BulgarianDemoDebtCollector: React.FC = () => {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+359');
  const [amountOwed, setAmountOwed] = useState('');
  const [dueDate, setDueDate] = useState('');
  const { toast } = useToast();

  const countryCodesData = useMemo(() => {
    return [
      { code: '+359', name: 'Bulgaria' },
      { code: '+1', name: 'United States/Canada' },
      { code: '+20', name: 'Egypt' },
      { code: '+27', name: 'South Africa' },
      { code: '+30', name: 'Greece' },
      { code: '+31', name: 'Netherlands' },
      { code: '+32', name: 'Belgium' },
      { code: '+33', name: 'France' },
      { code: '+34', name: 'Spain' },
      { code: '+36', name: 'Hungary' },
      { code: '+39', name: 'Italy' },
      { code: '+40', name: 'Romania' },
      { code: '+41', name: 'Switzerland' },
      { code: '+43', name: 'Austria' },
      { code: '+44', name: 'United Kingdom' },
      { code: '+45', name: 'Denmark' },
      { code: '+46', name: 'Sweden' },
      { code: '+47', name: 'Norway' },
      { code: '+48', name: 'Poland' },
      { code: '+49', name: 'Germany' },
      { code: '+51', name: 'Peru' },
      { code: '+52', name: 'Mexico' },
      { code: '+53', name: 'Cuba' },
      { code: '+54', name: 'Argentina' },
      { code: '+55', name: 'Brazil' },
      { code: '+56', name: 'Chile' },
      { code: '+57', name: 'Colombia' },
      { code: '+58', name: 'Venezuela' },
      { code: '+60', name: 'Malaysia' },
      { code: '+61', name: 'Australia' },
      { code: '+62', name: 'Indonesia' },
      { code: '+63', name: 'Philippines' },
      { code: '+64', name: 'New Zealand' },
      { code: '+65', name: 'Singapore' },
      { code: '+66', name: 'Thailand' },
      { code: '+81', name: 'Japan' },
      { code: '+82', name: 'South Korea' },
      { code: '+84', name: 'Vietnam' },
      { code: '+86', name: 'China' },
      { code: '+90', name: 'Turkey' },
      { code: '+91', name: 'India' },
      { code: '+92', name: 'Pakistan' },
      { code: '+93', name: 'Afghanistan' },
      { code: '+94', name: 'Sri Lanka' },
      { code: '+95', name: 'Myanmar' },
      { code: '+98', name: 'Iran' },
      { code: '+212', name: 'Morocco' },
      { code: '+213', name: 'Algeria' },
      { code: '+216', name: 'Tunisia' },
      { code: '+218', name: 'Libya' },
      { code: '+220', name: 'Gambia' },
      { code: '+221', name: 'Senegal' },
      { code: '+233', name: 'Ghana' },
      { code: '+234', name: 'Nigeria' },
      { code: '+254', name: 'Kenya' },
      { code: '+255', name: 'Tanzania' },
      { code: '+256', name: 'Uganda' },
      { code: '+260', name: 'Zambia' },
      { code: '+263', name: 'Zimbabwe' },
      { code: '+351', name: 'Portugal' },
      { code: '+352', name: 'Luxembourg' },
      { code: '+353', name: 'Ireland' },
      { code: '+354', name: 'Iceland' },
      { code: '+355', name: 'Albania' },
      { code: '+358', name: 'Finland' },
      { code: '+370', name: 'Lithuania' },
      { code: '+371', name: 'Latvia' },
      { code: '+372', name: 'Estonia' },
      { code: '+380', name: 'Ukraine' },
      { code: '+381', name: 'Serbia' },
      { code: '+385', name: 'Croatia' },
      { code: '+386', name: 'Slovenia' },
      { code: '+420', name: 'Czech Republic' },
      { code: '+421', name: 'Slovakia' },
      { code: '+593', name: 'Ecuador' },
      { code: '+852', name: 'Hong Kong' },
      { code: '+853', name: 'Macau' },
      { code: '+855', name: 'Cambodia' },
      { code: '+856', name: 'Laos' },
      { code: '+880', name: 'Bangladesh' },
      { code: '+886', name: 'Taiwan' },
      { code: '+960', name: 'Maldives' },
      { code: '+961', name: 'Lebanon' },
      { code: '+962', name: 'Jordan' },
      { code: '+963', name: 'Syria' },
      { code: '+964', name: 'Iraq' },
      { code: '+965', name: 'Kuwait' },
      { code: '+966', name: 'Saudi Arabia' },
      { code: '+967', name: 'Yemen' },
      { code: '+968', name: 'Oman' },
      { code: '+971', name: 'UAE' },
      { code: '+972', name: 'Israel' },
      { code: '+973', name: 'Bahrain' },
      { code: '+974', name: 'Qatar' },
      { code: '+975', name: 'Bhutan' },
      { code: '+976', name: 'Mongolia' },
      { code: '+977', name: 'Nepal' },
      { code: '+992', name: 'Tajikistan' },
      { code: '+993', name: 'Turkmenistan' },
      { code: '+994', name: 'Azerbaijan' },
      { code: '+995', name: 'Georgia' },
      { code: '+996', name: 'Kyrgyzstan' },
      { code: '+998', name: 'Uzbekistan' },
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone number (basic check)
    const sanitizedPhoneNumber = phoneNumber.replace(/[^\d]/g, '');
    if (sanitizedPhoneNumber.length < 8 || sanitizedPhoneNumber.length > 15) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number.',
        variant: 'destructive'
      });
      return;
    }

    // Validate date (must be in the past, but not today)
    if (!isDateValid(dueDate)) {
      toast({
        title: 'Invalid Date',
        description: 'Please select a date from the past (not including today).',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Make sure the date is in the correct format
      const formattedDate = format(new Date(dueDate), 'yyyy-MM-dd');

      // Explicitly define payload to avoid any issues
      const payload = {
        name,
        phone_number: `${countryCode}${sanitizedPhoneNumber}`,
        country_code: countryCode,
        amount_owed: parseFloat(amountOwed),
        due_date: formattedDate
      };

      console.log("Submitting data to Supabase:", payload);
      
      const { data, error } = await supabase
        .from('bulgarian_demo_debt_entries')
        .insert(payload);

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Submission successful:", data);

      toast({
        title: 'Submission Successful',
        description: 'Вашата демо заявка за събиране на дълг беше регистрирана.',
        variant: 'default'
      });

      // Reset form fields
      setName('');
      setPhoneNumber('');
      setAmountOwed('');
      setDueDate('');
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: 'Submission Failed',
        description: 'Възникна грешка при изпращане на информацията.',
        variant: 'destructive'
      });
    }
  };

  const getTodayDateString = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  const getYesterdayDateString = () => {
    return format(subDays(new Date(), 1), 'yyyy-MM-dd');
  };

  // Function to check if date is valid for selection (only past dates, not including today)
  const isDateValid = (date: string): boolean => {
    const selectedDate = new Date(date);
    return isPast(selectedDate) && !isToday(selectedDate);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-12">
      <div className="container max-w-md mx-auto px-4">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 mb-4 text-3xl font-bold font-ibm">
            <span className="text-brand-navy">Think</span>
            <span className="text-brand-teal">Tomorrow</span>
            <span className="text-brand-navy uppercase">.ai</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Демо: AI Виртуален Агент за Събиране на Дългове
          </h1>
          <p className="text-gray-600">
            Изпитайте как AI може да ви помогне ефективно да възстановите дължими средства
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-8 space-y-4">
          <div>
            <Label htmlFor="name">Въведете име</Label>
            <Input 
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Въведете име"
              required
            />
          </div>

          <div>
            <Label htmlFor="phone">Въведете телефонен номер</Label>
            <div className="flex gap-2">
              <Select
                value={countryCode}
                onValueChange={setCountryCode}
              >
                <SelectTrigger className="w-[80px]">
                  <div className="flex items-center justify-center">
                    {countryCode && countryCodeToIso[countryCode] && (
                      <ReactCountryFlag
                        countryCode={countryCodeToIso[countryCode]}
                        svg
                        style={{
                          width: '1.2em',
                          height: '1.2em',
                        }}
                      />
                    )}
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {countryCodesData.map((country) => (
                    <SelectItem 
                      key={country.code} 
                      value={country.code}
                      className="flex items-center gap-2"
                    >
                      <div className="flex items-center gap-2 w-full">
                        {countryCodeToIso[country.code] && (
                          <ReactCountryFlag
                            countryCode={countryCodeToIso[country.code]}
                            svg
                            style={{
                              width: '1.2em',
                              height: '1.2em',
                            }}
                          />
                        )}
                        <span>{country.name}</span>
                        <span className="ml-auto text-gray-500">{country.code}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input 
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Въведете телефонен номер"
                className="flex-1"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="amount">Въведете демо дължима сума</Label>
            <Input 
              id="amount"
              type="number"
              value={amountOwed}
              onChange={(e) => setAmountOwed(e.target.value)}
              placeholder="Въведете дължима сума"
              min="0"
              step="0.01"
              required
            />
          </div>

          <div>
            <Label htmlFor="dueDate">Демо краен срок (изберете дата в миналото)</Label>
            <Input 
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              max={getYesterdayDateString()}
              required
            />
            {dueDate && !isDateValid(dueDate) && (
              <p className="text-red-500 text-sm mt-1">
                Моля изберете дата в миналото (не включвайки днешната дата)
              </p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full"
            disabled={dueDate && !isDateValid(dueDate)}
          >
            Изпрати демо за AI събиране на дълг
          </Button>
        </form>
      </div>
    </div>
  );
};

export default BulgarianDemoDebtCollector;
